// ============================================================================
// FSA TRIAL BALANCE SERVICE
// ============================================================================
// Wraps the multipart upload + preview endpoints, plus the read endpoints
// for the engagement screen's TB section.

import axiosClient, { get, post } from '../../client/axiosClient';
import { fsaTrialBalanceEndpoints } from '../../endpoints/fsa/fsaTrialBalance.endpoints';
import { buildEndpoint, buildEndpointWithQuery } from '../../endpoints/base.endpoints';
import type { ApiResponse, ID, PaginatedResponse } from '../../types';
import type {
  TrialBalance,
  TrialBalanceEntry,
  TrialBalancePreviewResponse,
  TrialBalanceSource,
  TrialBalanceEntryQuery,
  TrialBalanceColumnMap,
  SourceTbRegistryItem,
  SourceReplacementImpact,
  UploadResult,
  MarkControlAccountRequest,
  MarkExcludeFromSignSweepRequest,
  ControlAccountSummary,
  TrialBalanceAuditedExportRow,
} from '../../types/fsa/trialBalance.types';

class FsaTrialBalanceService {
  async preview(
    file: File,
    source: TrialBalanceSource,
    columnMap?: TrialBalanceColumnMap | null
  ): Promise<ApiResponse<TrialBalancePreviewResponse>> {
    const form = new FormData();
    form.append('file', file);
    form.append('source', source);
    if (columnMap) form.append('columnMap', JSON.stringify(columnMap));
    const response = await axiosClient.post<ApiResponse<TrialBalancePreviewResponse>>(
      fsaTrialBalanceEndpoints.preview(),
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  /**
   * FSA-2.1.6 — parses the file and computes the per-source delta against
   * the source being replaced, WITHOUT committing. Used by the upload
   * wizard's Impact step. `replaceSourceId` is required (adding a new
   * source has nothing to diff).
   */
  async previewImpact(
    engagementId: ID,
    file: File,
    source: TrialBalanceSource,
    name: string,
    replaceSourceId: ID,
    columnMap?: TrialBalanceColumnMap | null
  ): Promise<ApiResponse<SourceReplacementImpact>> {
    const form = new FormData();
    form.append('engagementId', String(engagementId));
    form.append('name', name);
    form.append('replaceSourceId', String(replaceSourceId));
    form.append('source', source);
    form.append('file', file);
    if (columnMap) form.append('columnMap', JSON.stringify(columnMap));
    const response = await axiosClient.post<ApiResponse<SourceReplacementImpact>>(
      fsaTrialBalanceEndpoints.impactPreview(),
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  /**
   * FSA-2.1.6 — uploads a SOURCE TB. When revising an existing named TB,
   * pass `replaceSourceId`; when adding a new TB for consolidation, omit
   * it (the name must be unique within the engagement).
   * Response includes the rebuilt consolidated TB AND a per-source-delta
   * impact summary for the modal's impact step.
   */
  async upload(
    engagementId: ID,
    file: File,
    source: TrialBalanceSource,
    name: string,
    replaceSourceId?: ID | null,
    periodStart?: string | null,
    periodEnd?: string | null,
    purpose: 'CURRENT' | 'OPENING' = 'CURRENT',
    columnMap?: TrialBalanceColumnMap | null
  ): Promise<ApiResponse<UploadResult>> {
    const form = new FormData();
    form.append('engagementId', String(engagementId));
    form.append('name', name);
    if (replaceSourceId) form.append('replaceSourceId', String(replaceSourceId));
    form.append('source', source);
    form.append('file', file);
    if (periodStart) form.append('periodStart', periodStart);
    if (periodEnd) form.append('periodEnd', periodEnd);
    form.append('purpose', purpose);
    if (columnMap) form.append('columnMap', JSON.stringify(columnMap));
    const response = await axiosClient.post<ApiResponse<UploadResult>>(
      fsaTrialBalanceEndpoints.upload(),
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  /** Downloads the blank .xlsx upload template and triggers a browser save. */
  async downloadUploadTemplate(): Promise<void> {
    const response = await axiosClient.get(fsaTrialBalanceEndpoints.uploadTemplate(), { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'PRIMA-TB-Upload-Template.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Downloads the engagement's "Note Reconciliation & Ledger Breakup" .xlsx
   * (Summary: saved vs derived + drift; Detail: per-note ledger breakup +
   * cross-note/P&L reconciliation) and triggers a browser save.
   */
  async downloadNoteReconciliationReport(engagementId: ID): Promise<void> {
    const url = buildEndpoint(`/FsaWorkPaperReport/by-engagement/${engagementId}/note-reconciliation`);
    const response = await axiosClient.get(url, { responseType: 'blob' });
    const href = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = href;
    a.download = 'Note-Reconciliation-Ledger-Breakup.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(href);
  }

  /** FSA-2.1.6 — source TB registry. */
  async listSources(engagementId: ID): Promise<ApiResponse<SourceTbRegistryItem[]>> {
    const response = await get<ApiResponse<SourceTbRegistryItem[]>>(
      fsaTrialBalanceEndpoints.listSources(engagementId)
    );
    return response.data;
  }

  /** FSA-2.1.6 — remove a SOURCE TB from consolidation (consolidator rebuilds). */
  async removeSource(sourceId: ID): Promise<ApiResponse<TrialBalance>> {
    const response = await post<ApiResponse<TrialBalance>>(
      fsaTrialBalanceEndpoints.removeSource(sourceId),
      {}
    );
    return response.data;
  }

  /** FSA-2.1.6 — flip the control-account flag on consolidated entries. */
  async bulkControlAccount(
    tbId: ID,
    request: MarkControlAccountRequest
  ): Promise<ApiResponse<number>> {
    const response = await post<ApiResponse<number>>(
      fsaTrialBalanceEndpoints.bulkControlAccount(tbId),
      request
    );
    return response.data;
  }

  /**
   * FSA-2.2 — flip the sign-sweep opt-out flag on consolidated entries.
   * When TRUE, the auto cross-note sweep on Trade Payables / Trade
   * Receivables skips this ledger even if its sign opposes the schedule.
   */
  async bulkExcludeFromSignSweep(
    tbId: ID,
    request: MarkExcludeFromSignSweepRequest
  ): Promise<ApiResponse<number>> {
    const response = await post<ApiResponse<number>>(
      fsaTrialBalanceEndpoints.bulkExcludeFromSignSweep(tbId),
      request
    );
    return response.data;
  }

  async controlAccountSummary(tbId: ID): Promise<ApiResponse<ControlAccountSummary>> {
    const response = await get<ApiResponse<ControlAccountSummary>>(
      fsaTrialBalanceEndpoints.controlAccountSummary(tbId)
    );
    return response.data;
  }

  /**
   * FSA-2.3 — fetches the row set for the Audited Closing TB export
   * (consolidated entries merged with posted Adjustment Journal effects).
   * The TB tab's Export button calls this and dumps the rows to XLSX.
   */
  async exportAudited(tbId: ID): Promise<ApiResponse<TrialBalanceAuditedExportRow[]>> {
    const response = await get<ApiResponse<TrialBalanceAuditedExportRow[]>>(
      fsaTrialBalanceEndpoints.exportAudited(tbId)
    );
    return response.data;
  }

  async listByEngagement(engagementId: ID): Promise<ApiResponse<TrialBalance[]>> {
    const response = await get<ApiResponse<TrialBalance[]>>(
      fsaTrialBalanceEndpoints.listByEngagement(engagementId)
    );
    return response.data;
  }

  async getCurrent(engagementId: ID): Promise<ApiResponse<TrialBalance | null>> {
    const response = await get<ApiResponse<TrialBalance | null>>(
      fsaTrialBalanceEndpoints.getCurrent(engagementId)
    );
    return response.data;
  }

  async getById(id: ID): Promise<ApiResponse<TrialBalance>> {
    const response = await get<ApiResponse<TrialBalance>>(
      fsaTrialBalanceEndpoints.getById(id)
    );
    return response.data;
  }

  async getEntries(id: ID, query?: TrialBalanceEntryQuery): Promise<PaginatedResponse<TrialBalanceEntry>> {
    const endpoint = query
      ? buildEndpointWithQuery(`/FsaTrialBalances/${id}/entries`, query as Record<string, any>)
      : fsaTrialBalanceEndpoints.getEntries(id);
    const response = await get<any>(endpoint);
    const raw = response.data;
    const paged = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: paged.items ?? [],
      pagination: {
        currentPage: paged.page ?? 1,
        pageSize: paged.pageSize ?? 20,
        totalPages: paged.totalPages ?? 0,
        totalItems: paged.totalCount ?? 0,
        hasNextPage: paged.hasNext ?? false,
        hasPreviousPage: paged.hasPrevious ?? false,
      },
    };
  }

  async finalize(id: ID): Promise<ApiResponse<TrialBalance>> {
    const response = await post<ApiResponse<TrialBalance>>(
      fsaTrialBalanceEndpoints.finalize(id),
      {}
    );
    return response.data;
  }
}

export const fsaTrialBalanceService = new FsaTrialBalanceService();
export default fsaTrialBalanceService;
